import React, { createContext, useState, useEffect, useContext } from 'react';

// Create authentication context
const AuthContext = createContext(null);

// Use relative URL by default so it works in both dev and production
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch the user's authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user`, {
          credentials: 'include', // Important for cookies
        });

        if (!response.ok) {
          if (response.status === 401) {
            setUser(null);
          } else {
            throw new Error('Error checking authentication status');
          }
        } else {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (err) {
        setError(err.message);
        console.error('Auth check error:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function - redirects to Google OAuth
  const login = () => {
    // For relative URLs, use the current window location to construct the full auth URL
    if (API_BASE_URL.startsWith('/')) {
      window.location.href = `/auth/google`;
    } else {
      // For absolute URLs, use the provided base URL
      const authUrl = new URL(API_BASE_URL);
      window.location.href = `${authUrl.protocol}//${authUrl.host}/auth/google`;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        credentials: 'include'
      });
      setUser(null);
      window.location.reload();
    } catch (err) {
      setError(err.message);
      console.error('Logout error:', err);
    }
  };

  // Auth context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
